import { useCallback, useRef, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { Socket } from "socket.io-client";
import { debounce } from "../../../lib/debounce";
import { signUpload } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { askConfirm, toast } from "../../../lib/ui";
import type { Msg } from "../../../types/chat";
import type { useComposer } from "../composer/useComposer";
import type { useUploads } from "../composer/useUploads";
import { safeUuid } from "../chatUtils";
import type { OutboxItem, useOutbox } from "./useOutbox";

const k = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

/**
 * Composer and message actions: typing indicator, sending, editing, deleting, reacting, and
 * attaching files (composer uploads, remote images/GIFs, gallery photos). State stays in
 * ChatLayout; this owns the handlers.
 */
export function useMessageActions({
  socket,
  currentVoidId,
  currentChannelId,
  userId,
  text,
  setText,
  inputRef,
  spoiler,
  setSpoiler,
  replyTo,
  setReplyTo,
  editingId,
  setEditingId,
  editText,
  setEditText,
  setPickerFor,
  uploading,
  pendingUploads,
  addUploads,
  clearPendingUploads,
  setPendingUploads,
  setMsgsByKey,
  emitOrQueue,
  refreshPreview,
  galleryFileRef,
}: {
  socket: Socket;
  currentVoidId: string;
  currentChannelId: string;
  userId?: string;
  text: ReturnType<typeof useComposer<Msg>>["text"];
  setText: ReturnType<typeof useComposer<Msg>>["setText"];
  inputRef: ReturnType<typeof useComposer<Msg>>["inputRef"];
  spoiler: ReturnType<typeof useComposer<Msg>>["spoiler"];
  setSpoiler: ReturnType<typeof useComposer<Msg>>["setSpoiler"];
  replyTo: ReturnType<typeof useComposer<Msg>>["replyTo"];
  setReplyTo: ReturnType<typeof useComposer<Msg>>["setReplyTo"];
  editingId: ReturnType<typeof useComposer<Msg>>["editingId"];
  setEditingId: ReturnType<typeof useComposer<Msg>>["setEditingId"];
  editText: ReturnType<typeof useComposer<Msg>>["editText"];
  setEditText: ReturnType<typeof useComposer<Msg>>["setEditText"];
  setPickerFor: (v: string | null) => void;
  uploading: ReturnType<typeof useUploads>["uploading"];
  pendingUploads: ReturnType<typeof useUploads>["pendingUploads"];
  addUploads: ReturnType<typeof useUploads>["addUploads"];
  clearPendingUploads: ReturnType<typeof useUploads>["clearPending"];
  setPendingUploads: ReturnType<typeof useUploads>["setPendingUploads"];
  setMsgsByKey: Dispatch<SetStateAction<Record<string, Msg[]>>>;
  emitOrQueue: ReturnType<typeof useOutbox>["emitOrQueue"];
  refreshPreview: (fqChanId: string) => unknown;
  galleryFileRef: RefObject<HTMLInputElement | null>;
}) {
  const sendTypingTrue  = () => socket.emit("typing:set", { voidId: currentVoidId, channelId: fq(currentVoidId, currentChannelId), isTyping: true });
  const sendTypingFalse = () => socket.emit("typing:set", { voidId: currentVoidId, channelId: fq(currentVoidId, currentChannelId), isTyping: false });
  const debouncedStop   = debounce(sendTypingFalse, 1500);
  const lastTypingTrueAtRef = useRef<number>(0);
  function onInputChange(v: string) {
    setText(v);
    const now = Date.now();
    if (now - (lastTypingTrueAtRef.current || 0) > 1000) { // throttle true to 1s
      lastTypingTrueAtRef.current = now;
      sendTypingTrue();
    }
    debouncedStop();
  }

  function insertEmojiAtCaret(e: string) {
    const el = inputRef.current;
    if (!el) { setText(t => (t + e)); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + e + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + e.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function send() {
    const content = text.trim();
    if (uploading) return;
    if (!content && pendingUploads.length === 0) return;
    const tempId = safeUuid();
    const kk = k(currentVoidId, currentChannelId);
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      return { ...old, [kk]: [...list, { id: tempId, content, optimistic: true, attachments: pendingUploads, spoiler: !!spoiler, replyTo: replyTo ? { id: replyTo.id, authorId: replyTo.authorId, authorName: replyTo.authorName, authorColor: replyTo.authorColor ?? null, content: replyTo.content } : null }] };
    });
    const payload: OutboxItem = { voidId: currentVoidId, channelId: fq(currentVoidId, currentChannelId), content, tempId, attachments: pendingUploads, replyToId: replyTo?.id || null, spoiler: !!spoiler };
    emitOrQueue(payload);
    setText(""); clearPendingUploads(); setReplyTo(null); setSpoiler(false); sendTypingFalse();
  }

  async function onFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) return;
    const token = getAuthToken() || '';
    const MAX = 10 * 1024 * 1024; // 10MB
    const filtered = Array.from(files).filter((f) => f.size <= MAX);
    if (filtered.length === 0) return;
    await addUploads(filtered, token);
  }

  // Fetch a remote image/GIF and attach it by uploading to our storage
  async function addRemoteAsUpload(remoteUrl: string, fallbackName?: string) {
    try {
      const r = await fetch(remoteUrl, { mode: 'cors' });
      if (!r.ok) throw new Error('fetch failed');
      const blob = await r.blob();
      const type = blob.type || 'image/gif';
      const ext = type.startsWith('image/') ? type.split('/')[1] : 'gif';
      const name = fallbackName || `image-${Date.now()}.${ext}`;
      const token = getAuthToken() || '';
      const { url, headers, publicUrl } = await signUpload({ filename: name, contentType: type, size: blob.size, purpose: "message" }, token);
      await fetch(url, { method: 'PUT', headers, body: blob });
      // Chat uploads are private, so an <img> can't load publicUrl; preview from the fetched bytes instead.
      setPendingUploads(prev => [...prev, { url: publicUrl, contentType: type, name, size: blob.size, localPreviewUrl: URL.createObjectURL(blob) }]);
    } catch (e) {
      console.error('Failed to import remote image', e);
      // As a last resort, show the remote image URL directly
      setPendingUploads(prev => [...prev, { url: remoteUrl, contentType: 'image/gif', name: fallbackName || 'image.gif', size: 0 }]);
    }
  }

  function isMine(m: Msg) {
    return !!m.optimistic || (!!m.authorId && m.authorId === userId);
  }

  function startEdit(m: Msg) {
    if (!isMine(m)) return;
    try { setPickerFor(null); } catch { /* noop */ }
    setEditingId(m.id);
    setEditText(m.content);
  }

  // Add images directly to a gallery channel
  async function addGalleryFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    try {
      const token = getAuthToken() || '';
      const atts: { url: string; contentType?: string; name?: string; size?: number }[] = [];
      for (const f of Array.from(files)) {
        const name = f.name || 'image.jpg';
        const lower = name.toLowerCase();
        const type = (f.type || '').toLowerCase();
        const isPngJpg = type === 'image/png' || type === 'image/jpeg' || /\.(png|jpe?g)(\?.*)?$/.test(lower);
        if (!isPngJpg) continue; // skip non-png/jpeg (e.g., gif)
        const signType = type || (lower.endsWith('.png') ? 'image/png' : 'image/jpeg');
        const up = await signUpload({ filename: name, contentType: signType, size: f.size, purpose: "message" }, token);
        await fetch(up.url, { method: 'PUT', headers: up.headers, body: f });
        atts.push({ url: up.publicUrl, contentType: signType, name, size: f.size });
      }
      if (atts.length === 0) return;
      const tempId = safeUuid();
      const kk = k(currentVoidId, currentChannelId);
      setMsgsByKey(old => ({ ...old, [kk]: [...(old[kk] ?? []), { id: tempId, content: '', optimistic: true, attachments: atts }] }));
      emitOrQueue({ voidId: currentVoidId, channelId: fq(currentVoidId, currentChannelId), content: '', tempId, attachments: atts });
      // Ensure landing favorites gallery widget refreshes even for our own uploads
      try { refreshPreview(fq(currentVoidId, currentChannelId)); } catch { /* noop */ }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to add photos';
        toast(msg || 'Failed to add photos', 'error');
    } finally {
      try { if (galleryFileRef.current) galleryFileRef.current.value = ''; } catch { /* noop */ }
    }
  }

  function saveEdit() {
    const mid = editingId; if (!mid) return;
    const content = editText.trim();
    if (content.length === 0) return;
    socket.emit("message:edit", { messageId: mid, content });
    // optimistic update
    const kk = k(currentVoidId, currentChannelId);
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      const idx = list.findIndex(m => m.id === mid);
      if (idx === -1) return old;
      const copy = [...list];
      copy[idx] = { ...copy[idx], content, optimistic: true };
      return { ...old, [kk]: copy };
    });
    // Close editor immediately after saving; server event will clear optimistic flag
    setEditingId(null);
    setEditText("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditText("");
  }

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    const ok = await askConfirm({ title:'Delete Message', message:'Delete this message?', confirmText:'Delete', cancelText:'Cancel' });
    if (!ok) return;
    socket.emit('message:delete', { messageId });
  }, [socket]);

  function toggleReaction(m: Msg, emoji: string) {
    const kk = k(currentVoidId, currentChannelId);
    const mine = !!m.reactions?.[emoji]?.mine;
    // optimistic update
    setMsgsByKey((old) => {
      const list = old[kk] ?? [];
      const idx = list.findIndex(x => x.id === m.id);
      if (idx === -1) return old;
      const copy = [...list];
      const prev = copy[idx].reactions || {};
      const cur = prev[emoji] || { count: 0, mine: false };
      const nextCount = Math.max(0, cur.count + (mine ? -1 : 1));
      const next = { ...prev, [emoji]: { count: nextCount, mine: !mine } };
      if (nextCount === 0) { delete next[emoji]; }
      copy[idx] = { ...copy[idx], reactions: next };
      return { ...old, [kk]: copy };
    });
    if (mine) socket.emit("reaction:remove", { messageId: m.id, emoji });
    else socket.emit("reaction:add", { messageId: m.id, emoji });
  }

  return {
    onInputChange,
    insertEmojiAtCaret,
    send,
    onFilesSelected,
    addRemoteAsUpload,
    isMine,
    startEdit,
    addGalleryFiles,
    saveEdit,
    cancelEdit,
    handleDeleteMessage,
    toggleReaction,
  };
}
